import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PASystemCard from '../components/PASystemCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Filter, Package, Music, Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Browse = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSystems();
  }, []);

  const fetchSystems = async () => {
    if (!BACKEND_URL) {
      setError('Backend URL not configured');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/systems`);
      setSystems(Array.isArray(response?.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch systems:', err);
      setError('Failed to load PA systems');
      setSystems([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredSystems = (systems || []).filter(system => {
    if (!system) return false;
    const matchesSearch = system?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase() || '') ||
                         system?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase() || '');
    const matchesCategory = categoryFilter === 'all' || system?.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-yellow-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading PA systems...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={fetchSystems} className="bg-yellow-500 hover:bg-yellow-600 text-black">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-white mb-4 flex items-center gap-3">
              <Music className="h-10 w-10 text-yellow-500" />
              Browse PA Systems
            </h1>
            <p className="text-xl text-gray-400">
              Choose the perfect sound system for your event
            </p>
          </div>
          <Button
            onClick={() => navigate('/equipment')}
            variant="outline"
            className="flex items-center gap-2 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black transition-colors"
            data-testid="browse-equipment-btn"
          >
            <Package className="h-5 w-5" />
            Browse Individual Equipment
          </Button>
        </div>

        {/* Filters */}
        <div className="mb-8 space-y-4 md:space-y-0 md:flex md:gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
            <Input
              type="text"
              placeholder="Search PA systems..."
              className="pl-10 py-6 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="md:w-64">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="py-6 bg-gray-800 border-gray-700 text-white">
                <Filter className="h-4 w-4 mr-2 text-yellow-500" />
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="all" className="text-white hover:bg-gray-700">All Categories</SelectItem>
                <SelectItem value="Basic" className="text-white hover:bg-gray-700">Basic</SelectItem>
                <SelectItem value="Premium" className="text-white hover:bg-gray-700">Premium</SelectItem>
                <SelectItem value="Professional" className="text-white hover:bg-gray-700">Professional</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-400">
            Showing <span className="font-semibold text-yellow-500">{filteredSystems.length}</span> PA system{filteredSystems.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Systems Grid */}
        {filteredSystems.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredSystems.map(system => (
              <PASystemCard key={system.system_id} system={system} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Music className="h-16 w-16 text-gray-700 mx-auto mb-4" />
            <p className="text-xl text-gray-500">No PA systems found matching your criteria</p>
            <Button
              className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-black"
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
              }}
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Browse;
